import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { cn } from '../../lib/utils';
import defaultTheme from 'tailwindcss/defaultTheme';
import type {
  DialogOverlayProps,
  DialogContentProps,
  DialogActionsProps,
  DialogTitleProps,
  DialogDescriptionProps,
} from './types';
import useI18n from '../../hooks/useI18n';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...rest }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'eye:fixed eye:inset-[0] eye:z-1300 eye:grid eye:bg-black/80 eye:px-8 eye:py-12 eye:pt-xl' +
        ' eye:transition-opacity eye:duration-200 eye:ease-out eye:data-[state=closed]:pointer-events-none eye:data-[state=closed]:opacity-0 eye:data-[state=open]:opacity-100 eye:sm:py-64',
      'eye:place-items-center',
      'eye:overflow-y-auto',
      className,
    )}
    {...rest}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      maxWidth = 'sm',
      persistent = false,
      style,
      ...rest
    },
    ref,
  ) => {
    const { t } = useI18n();

    return (
      <DialogPortal>
        <DialogOverlay>
          <DialogPrimitive.Content
            ref={ref}
            data-slot='dialog-content'
            className={cn(
              'eye:bg-white',
              'eye:p-s eye:md:p-l',
              'eye:rounded-lg eye:border eye:transition-all eye:duration-200 eye:ease-out eye:data-[state=closed]:scale-95 eye:data-[state=closed]:opacity-0 eye:data-[state=open]:scale-100 eye:data-[state=open]:opacity-100',
              'group/dialog-content',
              className,
            )}
            style={{
              maxWidth: `calc(${defaultTheme.screens[maxWidth]} - 32px)`,
              ...style,
            }}
            data-has-close-button={!persistent}
            {...(persistent && {
              onEscapeKeyDown: (e) => e.preventDefault(),
              onPointerDownOutside: (e) => e.preventDefault(),
            })}
            {...rest}
          >
            {children}
            {!persistent && (
              <DialogPrimitive.Close
                asChild
                className='eye:absolute eye:top-xs eye:right-xs eye:disabled:pointer-events-none'
              >
                <Button variant='ghost' size='icon'>
                  <X className='eye:h-24 eye:w-24' />
                  <span className='eye:sr-only'>{t('close')}</span>
                </Button>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogActions = ({
  className,
  children,
  ...rest
}: DialogActionsProps) => (
  <div
    className={cn(
      'eye:mt-xs eye:flex eye:flex-row eye:items-center eye:justify-end eye:gap-xs eye:max-sm:justify-center',
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);
DialogActions.displayName = 'DialogActions';

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(({ className, children, ...rest }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'eye:text-lg eye:tracking-tight eye:mb-xs eye:pr-2xs eye:text-center eye:leading-none eye:font-semibold eye:sm:text-left',
      className,
    )}
    {...rest}
  >
    <Typography variant='headline-l'>{children}</Typography>
  </DialogPrimitive.Title>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(({ className, children, ...rest }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    data-slot='dialog-description'
    className={cn(
      'eye:mt-xs eye:mb-2xs eye:text-center eye:sm:text-left',
      'group-data-[has-close-button=true]/dialog-content:eye:mt-2xs',
      className,
    )}
    asChild
    {...rest}
  >
    <Typography variant='body-m' tag='div'>
      {children}
    </Typography>
  </DialogPrimitive.Description>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogActions,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogDescription,
};
